import { useState } from "react";
import { Space, Tooltip, message, Popconfirm, Input, Typography, Button } from "antd";
import { DeleteOutlined } from "@ant-design/icons";

const usePendingRequests = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [newRequest, setNewRequest] = useState(null);

  // Initialize requests with mock data
  const initialRequestsData = {
    data: [
      { id: 1, requestType: "Site member", requester: "Trainee 111", status: "Pending" },
      { id: 2, requestType: "DivisionManager", requester: "Mr Division", status: "Approved" },
      { id: 3, requestType: "Team Leader", requester: "Mr Lead", status: "Pending" },
      { id: 4, requestType: "Project Manager", requester: "Mr Project Manager", status: "Pending" },
    ],
    meta: {
      page: 1,
      take: 10,
      hasPrevious: false,
      hasNext: false,
    },
  };

  const [requests, setRequests] = useState(initialRequestsData);

  // Handle saving a request
  const handleSave = async (id) => {
    if (newRequest) {
      setRequests((prevRequests) => ({
        ...prevRequests,
        data: prevRequests.data.map((request) =>
          request.id === id ? { ...newRequest, ...selectedRequest } : request
        ),
      }));
      setNewRequest(null);
    } else {
      setRequests((prevRequests) => ({
        ...prevRequests,
        data: prevRequests.data.map((request) =>
          request.id === id ? { ...request, ...selectedRequest } : request
        ),
      }));
    }

    setEditingKey(null);
    message.success("Request saved successfully");
  };

  // Handle changing the status to accepted
  const handleAcceptRequest = async (id, e) => {
    e.stopPropagation();
    try {
      setRequests((prevRequests) => ({
        ...prevRequests,
        data: prevRequests.data.map((request) =>
          request.id === id ? { ...request, status: "Accepted" } : request
        ),
      }));
      message.success("Request accepted successfully");
    } catch (error) {
      console.log(error);
      message.warning("An error occurred while accepting the request. Please try again later.");
    }
  };

  // Handle changing the status to declined
  const handleDeclineRequest = async (id, e) => {
    e.stopPropagation();
    try {
      setRequests((prevRequests) => ({
        ...prevRequests,
        data: prevRequests.data.map((request) =>
          request.id === id ? { ...request, status: "Declined" } : request
        ),
      }));
      message.success("Request declined successfully");
    } catch (error) {
      console.log(error);
      message.warning("An error occurred while declining the request. Please try again later.");
    }
  };

  // Handle deleting a request
  const handleDeleteRequest = async (id, e) => {
    e.stopPropagation();
    try {
      setRequests((prevRequests) => ({
        ...prevRequests,
        data: prevRequests.data.filter((request) => request.id !== id),
      }));
      message.success("Request deleted successfully");
    } catch (error) {
      console.log(error);
      message.warning("An error occurred while deleting the request. Please try again later.");
    }
  };

  // Cancel action for Popconfirm
  const cancel = (e) => {
    e.stopPropagation();
  };

  // Determine row class based on request status
  const rowClassName = (record) => {
    return record.status === "Declined" ? "ant-table-row-declined" : "";
  };

  // Define table columns
  const columns = [
    {
      title: "Account Type",
      dataIndex: "requestType",
      key: "requestType",
      render: (text, record) => {
        return editingKey === record.id ? (
          <Input
            value={selectedRequest?.requestType}
            onChange={(e) => setSelectedRequest({ ...selectedRequest, requestType: e.target.value })}
          />
        ) : (
          text
        );
      },
    },
    {
      title: "Requester",
      dataIndex: "requester",
      key: "requester",
      render: (text, record) => {
        return editingKey === record.id ? (
          <Input
            value={selectedRequest?.requester}
            onChange={(e) => setSelectedRequest({ ...selectedRequest, requester: e.target.value })}
          />
        ) : (
          text
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text, record) => {
        return editingKey === record.id ? (
          <Input
            value={selectedRequest?.status}
            onChange={(e) => setSelectedRequest({ ...selectedRequest, status: e.target.value })}
          />
        ) : (
          text
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space size="middle">
          {editingKey === record.id ? (
            <span>
              <Typography.Link
                onClick={() => handleSave(record.id)}
                style={{ marginInlineEnd: 8 }}
              >
                Save
              </Typography.Link>
              <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
                <a>Cancel</a>
              </Popconfirm>
            </span>
          ) : (
            <>
              <Popconfirm
                title="Are you sure you want to delete this request?"
                onConfirm={(e) => handleDeleteRequest(record.id, e)}
                onCancel={cancel}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip title="Delete Request">
                  <DeleteOutlined
                    onClick={(e) => e.stopPropagation()}
                    className="icon DeleteOutlinediconColor"
                  />
                </Tooltip>
              </Popconfirm>

              {record.status === "Pending" && (
                <>
                  <Tooltip title="Accept Request">
                    <Button
                      type="primary"
                      onClick={(e) => handleAcceptRequest(record.id, e)}
                    >
                      Accept
                    </Button>
                  </Tooltip>
                  <Tooltip title="Decline Request">
                    <Button
                      danger
                      onClick={(e) => handleDeclineRequest(record.id, e)}
                    >
                      Decline
                    </Button>
                  </Tooltip>
                </>
              )}
            </>
          )}
        </Space>
      ),
    },
  ];

  return {
    requests,
    isLoading: false,
    isFetching: false,
    error: null,
    searchQuery,
    setSearchQuery,
    columns,
    onChange: (pagination, filters, sorter, extra) => {
      console.log("Table params:", pagination, filters, sorter, extra);
    },
    handleSearchChange: (e) => setSearchQuery(e.target.value),
    rowClassName,
  };
};

export default usePendingRequests;
